import { useState } from 'react'
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import Card from '../components/Card'

export default function ColorContrast() {
  const [foreground, setForeground] = useState('#000000')
  const [background, setBackground] = useState('#FFFFFF')

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null
  }

  const getLuminance = (r, g, b) => {
    const [rs, gs, bs] = [r, g, b].map(val => {
      val = val / 255
      return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
  }

  const getContrastRatio = (color1, color2) => {
    const rgb1 = hexToRgb(color1)
    const rgb2 = hexToRgb(color2)
    if (!rgb1 || !rgb2) return 0

    const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b)
    const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b)

    const lighter = Math.max(lum1, lum2)
    const darker = Math.min(lum1, lum2)

    return (lighter + 0.05) / (darker + 0.05)
  }

  const contrastRatio = getContrastRatio(foreground, background)

  const getRating = (ratio) => {
    if (ratio >= 7) return { level: 'AAA Large', pass: true, icon: CheckCircle, color: 'text-green-600' }
    if (ratio >= 4.5) return { level: 'AA Large / AAA Normal', pass: true, icon: CheckCircle, color: 'text-green-600' }
    if (ratio >= 3) return { level: 'AA Large', pass: true, icon: CheckCircle, color: 'text-yellow-600' }
    if (ratio >= 1) return { level: 'Fail', pass: false, icon: XCircle, color: 'text-red-600' }
    return { level: 'Fail', pass: false, icon: XCircle, color: 'text-red-600' }
  }

  const rating = getRating(contrastRatio)
  const Icon = rating.icon

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-3">Foreground Color:</label>
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="color"
                  value={foreground}
                  onChange={(e) => setForeground(e.target.value)}
                  className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={foreground}
                  onChange={(e) => setForeground(e.target.value)}
                  className="input flex-1"
                  placeholder="#000000"
                />
              </div>
              <div
                className="w-full h-16 rounded border-2 border-gray-200 flex items-center justify-center"
                style={{ backgroundColor: foreground }}
              >
                <span className="text-white font-semibold drop-shadow-lg">Sample Text</span>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-3">Background Color:</label>
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="color"
                  value={background}
                  onChange={(e) => setBackground(e.target.value)}
                  className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={background}
                  onChange={(e) => setBackground(e.target.value)}
                  className="input flex-1"
                  placeholder="#FFFFFF"
                />
              </div>
              <div
                className="w-full h-16 rounded border-2 border-gray-200"
                style={{ backgroundColor: background }}
              />
            </div>
          </div>

          <div
            className="w-full p-8 rounded-lg border-2 border-gray-200"
            style={{ backgroundColor: background }}
          >
            <p
              className="text-2xl font-bold"
              style={{ color: foreground }}
            >
              Sample Text on Background
            </p>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Contrast Ratio:</h3>
              <div className="flex items-center gap-2">
                <Icon className={`w-6 h-6 ${rating.color}`} />
                <span className={`text-2xl font-bold ${rating.color}`}>
                  {contrastRatio.toFixed(2)}:1
                </span>
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-5 h-5 ${rating.color}`} />
                <span className={`font-semibold ${rating.color}`}>
                  {rating.level}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {rating.pass
                  ? 'This color combination meets accessibility standards.'
                  : 'This color combination does not meet accessibility standards. Consider increasing the contrast ratio.'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">WCAG Guidelines:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Normal text: 4.5:1 (AA), 7:1 (AAA)</li>
                  <li>• Large text: 3:1 (AA), 4.5:1 (AAA)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Current Status:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Normal text: {contrastRatio >= 4.5 ? '✓' : '✗'} AA, {contrastRatio >= 7 ? '✓' : '✗'} AAA</li>
                  <li>• Large text: {contrastRatio >= 3 ? '✓' : '✗'} AA, {contrastRatio >= 4.5 ? '✓' : '✗'} AAA</li>
                </ul>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

